import { useEffect, useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { Link, useLocation, useRoute } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Check,
  X as XIcon,
  AlertTriangle,
  ImageOff,
  Sparkles,
  MoreVertical,
} from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ImageViewer } from '@/components/image-viewer';
import { InvoiceForm, type InvoiceFormState } from '@/components/invoice-form';

interface InvoiceDetail {
  invoice: {
    id: string;
    tenantId: string;
    tenantRucId: string;
    status: string;
    direction: 'purchase' | 'sale';
    tipoDocumento: InvoiceFormState['tipoDocumento'];
    isElectronicInvoice: boolean;
    cdc: string | null;
    invoiceNumber: string;
    timbrado: string;
    timbradoStart: string | null;
    timbradoEnd: string | null;
    issueDate: string;
    expiringDate: string | null;
    condition: 'contado' | 'credito';
    currency: string;
    senderRuc: string;
    senderRazonSocial: string;
    receiverRuc: string;
    receiverRazonSocial: string;
    subtotalBeforeTax: string;
    totalDiscounts: string;
    taxExempt: string;
    tax5: string;
    tax10: string;
    grandTotal: string;
    naturalezaOperacion: InvoiceFormState['naturalezaOperacion'] | null;
    categoriaGasto: InvoiceFormState['categoriaGasto'] | null;
    extractionProvider: 'gemini' | 'claude' | 'manual' | null;
    extractionPrecision: number | null;
    fieldConfidence: Record<string, number> | null;
    requiresHumanReviewReason: string | null;
    originalFileId: string | null;
    rejectionReason: string | null;
    // metadata es jsonb arbitrario; tipamos solo las claves que la UI
    // consume directamente. Resto se ignora.
    metadata: {
      invoice_purpose?: string;
      receiver_extracted?: {
        ruc: string | null;
        razon_social: string | null;
        mismatch: boolean;
      };
      set_lookup?: {
        source: 'cache' | 'set_api' | null;
        official_razon_social: string | null;
        llm_razon_social: string | null;
        mismatch: boolean;
        strong_mismatch?: boolean;
      };
    } | null;
  };
  tenant: {
    id: string;
    fullName: string;
    whatsappNumber: string;
  } | null;
  /**
   * Items NO se usan en el flow actual (para IVA/IRP solo importan los
   * totales que viven en `invoice`). El backend sigue devolviendo
   * `items: []` por retrocompat — lo aceptamos pero no lo renderizamos.
   */
  items: Array<unknown>;
  tenantRucs: Array<{ id: string; ruc: string; razonSocial: string }>;
  file: {
    id: string;
    mimeType: string;
    storageKey: string;
    originalFilename: string | null;
  } | null;
}

function toFormState(d: InvoiceDetail): InvoiceFormState {
  return {
    tipoDocumento: d.invoice.tipoDocumento,
    isElectronicInvoice: d.invoice.isElectronicInvoice,
    cdc: d.invoice.cdc ?? '',
    invoiceNumber: d.invoice.invoiceNumber,
    timbrado: d.invoice.timbrado,
    timbradoStart: d.invoice.timbradoStart ?? '',
    timbradoEnd: d.invoice.timbradoEnd ?? '',
    issueDate: d.invoice.issueDate,
    expiringDate: d.invoice.expiringDate ?? '',
    condition: d.invoice.condition,
    currency: d.invoice.currency,
    senderRuc: d.invoice.senderRuc,
    senderRazonSocial: d.invoice.senderRazonSocial,
    receiverRuc: d.invoice.receiverRuc,
    receiverRazonSocial: d.invoice.receiverRazonSocial,
    subtotalBeforeTax: d.invoice.subtotalBeforeTax,
    totalDiscounts: d.invoice.totalDiscounts,
    taxExempt: d.invoice.taxExempt,
    tax5: d.invoice.tax5,
    tax10: d.invoice.tax10,
    grandTotal: d.invoice.grandTotal,
    naturalezaOperacion: d.invoice.naturalezaOperacion ?? '',
    categoriaGasto: d.invoice.categoriaGasto ?? '',
  };
}

export function InvoiceReviewPage() {
  const [, navigate] = useLocation();
  const [, params] = useRoute('/admin/invoices/:id');
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const qc = useQueryClient();
  const invoiceId = params?.id ?? '';

  const [formState, setFormState] = useState<InvoiceFormState | null>(null);
  const [issues, setIssues] = useState<Record<string, string[]>>({});
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => api<InvoiceDetail>(`/api/admin/invoices/${invoiceId}`),
    enabled: !!me && !!invoiceId,
  });

  useEffect(() => {
    if (data && !formState) setFormState(toFormState(data));
  }, [data, formState]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!formState) throw new Error('no form');
      // receiverRuc + receiverRazonSocial NO se envían al server — son
      // fijos al tenant y el endpoint los strippea de todos modos
      // (defense in depth). Los excluimos acá para que el body sea
      // explícito sobre qué se modifica.
      const {
        receiverRuc: _ignoredRcvRuc,
        receiverRazonSocial: _ignoredRcvRazon,
        ...editable
      } = formState;
      void _ignoredRcvRuc;
      void _ignoredRcvRazon;
      const body = {
        ...editable,
        cdc: editable.cdc || null,
        timbradoStart: editable.timbradoStart || null,
        timbradoEnd: editable.timbradoEnd || null,
        expiringDate: editable.condition === 'credito' ? editable.expiringDate || null : null,
        naturalezaOperacion: editable.naturalezaOperacion || null,
        categoriaGasto: editable.categoriaGasto || null,
      };
      return api(`/api/admin/invoices/${invoiceId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      qc.invalidateQueries({ queryKey: ['review-queue'] });
      setIssues({});
      toast.success('Cambios guardados');
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.body.issues) {
        setIssues(err.body.issues);
        toast.error('Hay campos inválidos, revisá el formulario');
      } else {
        setIssues({ _form: ['No se pudo guardar'] });
        toast.error('No se pudo guardar la factura');
      }
    },
  });

  const reExtractMutation = useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/api/admin/invoices/${invoiceId}/re-extract`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Reintento encolado — el resultado llega en unos segundos');
      // Refresh hasta que el worker termine (status pasa de pending_extraction a otra cosa).
      setTimeout(() => qc.invalidateQueries({ queryKey: ['invoice', invoiceId] }), 3000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['invoice', invoiceId] }), 8000);
    },
    onError: () => toast.error('No se pudo reintentar la extracción'),
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (formState) await saveMutation.mutateAsync();
      return api(`/api/admin/invoices/${invoiceId}/approve`, { method: 'POST' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['review-queue'] });
      qc.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      toast.success('Factura aprobada');
      navigate('/admin/review-queue');
    },
    onError: () => toast.error('No se pudo aprobar la factura'),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      api(`/api/admin/invoices/${invoiceId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['review-queue'] });
      setShowReject(false);
      toast.success('Factura rechazada');
      navigate('/admin/review-queue');
    },
    onError: () => toast.error('No se pudo rechazar la factura'),
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }
  if (isLoading || !formState)
    return (
      <AdminShell hideHeader>
        <div className="text-muted-foreground">Cargando factura…</div>
      </AdminShell>
    );
  if (isError || !data) {
    return (
      <AdminShell hideHeader>
        <div className="text-destructive">No se pudo cargar la factura.</div>
        <Button variant="link" asChild>
          <Link href="/admin/review-queue">
            <ArrowLeft />
            Volver a la cola
          </Link>
        </Button>
      </AdminShell>
    );
  }

  const fileUrl = data.file ? `/api/admin/files/${data.file.id}` : null;
  const canApprove = (['pending_review', 'rejected'] as const).includes(
    data.invoice.status as 'pending_review' | 'rejected',
  );
  const canReject = (['pending_review', 'approved'] as const).includes(
    data.invoice.status as 'pending_review' | 'approved',
  );

  return (
    <AdminShell hideHeader>
      {/* Layout split: imagen ~60% del ancho a la izquierda, columna derecha
          con meta + acciones arriba (sticky) y form abajo (scroll independiente).
          Altura DEFINIDA = viewport menos el margen del SidebarInset (variant
          inset → m-2 = 1rem vertical). Tiene que ser fija (no flex-1): el shell
          es min-h-svh, así que flex-1 no acota la altura y el form crece y
          desborda la página. Con altura fija, cada columna scrollea adentro. */}
      <div className="grid grid-cols-[3fr_2fr] gap-4 h-[calc(100svh-1rem)] min-h-[500px]">
        {/* ── Columna izquierda — VISOR de factura ── */}
        <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
          {fileUrl ? (
            <ImageViewer src={fileUrl} alt="Factura" />
          ) : (
            <div className="flex h-full items-center justify-center flex-col gap-2 text-muted-foreground text-sm">
              <ImageOff className="size-8" />
              <p>Sin imagen original (factura cargada manualmente sin foto)</p>
            </div>
          )}
        </div>

        {/* ── Columna derecha — META + FORM ── */}
        <div className="flex flex-col rounded-lg border border-border bg-background shadow-sm overflow-hidden">
          {/* Header compacto con meta + acciones — sticky-ish (no scrollea) */}
          <div className="shrink-0 border-b border-border px-5 py-3 space-y-2">
            {/* Título + acciones: flex-wrap para que los botones bajen a otra
                línea cuando la columna es angosta (en vez de encimarse). */}
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold text-foreground truncate">
                  Factura {data.invoice.invoiceNumber}
                </h1>
                {(() => {
                  // Mostrar razón social + RUC del tenant (dato fiscal),
                  // no el teléfono/telegram. La razón social la sacamos del
                  // tenantRuc que corresponde a la factura (o el primario).
                  const ruc =
                    data.tenantRucs.find((r) => r.id === data.invoice.tenantRucId) ??
                    data.tenantRucs[0];
                  if (!ruc) return null;
                  return (
                    <p className="text-xs text-muted-foreground truncate">
                      {data.tenant ? (
                        <Link
                          href={`/admin/tenants/${data.tenant.id}`}
                          className="text-primary hover:underline"
                        >
                          {ruc.razonSocial}
                        </Link>
                      ) : (
                        ruc.razonSocial
                      )}{' '}
                      · <span className="font-mono">{ruc.ruc}</span>
                    </p>
                  );
                })()}
              </div>
              {/* Todas las acciones en un menú de 3 puntos para mantener el
                  header limpio. */}
              <div className="shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" title="Acciones">
                      <MoreVertical />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => saveMutation.mutate()}
                      disabled={saveMutation.isPending}
                    >
                      <Save />
                      {saveMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                    </DropdownMenuItem>
                    {(canApprove || canReject) && (
                      <>
                        <DropdownMenuSeparator />
                        {canApprove && (
                          <DropdownMenuItem
                            onClick={() => approveMutation.mutate()}
                            disabled={approveMutation.isPending}
                          >
                            <Check />
                            Aprobar
                          </DropdownMenuItem>
                        )}
                        {canReject && (
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => setShowReject(true)}
                            disabled={rejectMutation.isPending}
                          >
                            <XIcon />
                            Rechazar
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge
                variant={
                  data.invoice.status === 'approved'
                    ? 'success'
                    : data.invoice.status === 'rejected'
                      ? 'destructive'
                      : 'warning'
                }
              >
                {data.invoice.status}
              </Badge>
              {data.invoice.extractionProvider && (
                <Badge variant="default">
                  <Sparkles className="size-3" />
                  {data.invoice.extractionProvider}
                  {data.invoice.extractionPrecision !== null && ` (${data.invoice.extractionPrecision}%)`}
                </Badge>
              )}
              {data.invoice.requiresHumanReviewReason && (
                <Badge variant="warning">
                  <AlertTriangle className="size-3" />
                  {data.invoice.requiresHumanReviewReason}
                </Badge>
              )}
            </div>
          </div>

          {/* Form con scroll independiente */}
          <div className="flex-1 overflow-y-auto p-5">
            <InvoiceForm
              value={formState}
              onChange={setFormState}
              fieldConfidence={data.invoice.fieldConfidence}
              issues={issues}
              direction={data.invoice.direction}
              // Para COMPRAS: el receptor es el tenant (FIJO).
              // Para VENTAS: no se pasa — el receptor es el cliente
              // y viene del formState (receiverRuc/razonSocial).
              tenantReceiver={data.invoice.direction === 'sale' ? undefined : (() => {
                const ruc = data.tenantRucs.find(
                  (r) => r.id === data.invoice.tenantRucId
                ) ?? data.tenantRucs[0];
                return ruc
                  ? { ruc: ruc.ruc, razonSocial: ruc.razonSocial }
                  : undefined;
              })()}
              receiverMismatch={
                data.invoice.direction !== 'sale' &&
                data.invoice.metadata?.receiver_extracted?.mismatch
                  ? {
                      extractedRuc: data.invoice.metadata.receiver_extracted.ruc,
                      extractedRazonSocial:
                        data.invoice.metadata.receiver_extracted.razon_social,
                    }
                  : undefined
              }
              razonSocialMismatch={
                data.invoice.metadata?.set_lookup?.strong_mismatch
                  ? {
                      llm: data.invoice.metadata.set_lookup.llm_razon_social,
                      oficial: data.invoice.metadata.set_lookup.official_razon_social,
                    }
                  : undefined
              }
              onReExtract={() => reExtractMutation.mutate()}
              isReExtracting={reExtractMutation.isPending}
            />
          </div>
        </div>
      </div>

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar factura</DialogTitle>
            <DialogDescription>
              Explicá brevemente por qué se rechaza. Esto se registra en el audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="ej: la imagen no corresponde a una factura legal, está duplicada, etc."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)} disabled={rejectMutation.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => rejectMutation.mutate()}
              disabled={rejectMutation.isPending || rejectReason.trim().length < 3}
            >
              {rejectMutation.isPending ? 'Rechazando…' : 'Confirmar rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
